import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  FiX, FiSearch, FiCalendar, FiUser, FiHash, FiDollarSign, FiCheck, FiRotateCcw, FiRepeat
} from 'react-icons/fi';
import { colors, borderRadius, typography, shadows } from '../../theme/colors';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import useCurrency from '../../hooks/useCurrency';
import { localDateStr } from '../../utils/localDate';

/**
 * Touch-friendly receipt picker for Return / Exchange flows.
 *
 * Replaces the legacy window.prompt path with a proper modal:
 *   - Multi-criteria filter (receipt#, customer, amount, date range)
 *   - Tappable filter chips for fast date selection
 *   - Big clickable rows with details summary
 *   - Sticky "SELECT" action at the bottom so touch users don't hunt for it
 *
 * Lists PS (POS Sale) only — you can't return / exchange a return.
 *
 * Props:
 *   isOpen           boolean
 *   mode             'return' | 'exchange'
 *   companyId        string
 *   activeBranchId   string | null
 *   onSelect(txn)    invoked when user confirms a row
 *   onClose()
 */
const DATE_PRESETS = [
  { id: 'today',   label: 'Today',   days: 0 },
  { id: 'y',       label: 'Yesterday', days: 1 },
  { id: 'w',       label: 'Last 7 days', days: 7 },
  { id: 'm',       label: 'Last 30 days', days: 30 },
  { id: 'all',     label: 'All time', days: null },
];

const POSReceiptPickerModal = ({ isOpen, mode = 'return', companyId, activeBranchId, activeCounterId, onSelect, onClose }) => {
  const { formatCurrency } = useCurrency();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchCustomer, setSearchCustomer] = useState('');
  const [searchAmount, setSearchAmount] = useState('');
  const [datePreset, setDatePreset] = useState('today');
  const [selectedId, setSelectedId] = useState(null);
  const searchRef = useRef(null);

  // Load PS transactions whenever the modal opens / company / branch changes.
  useEffect(() => {
    if (!isOpen || !companyId) return;
    setSelectedId(null);
    setLoading(true);
    (async () => {
      try {
        const txnRef = collection(db, 'companies', companyId, 'transactions');
        const q = query(
          txnRef,
          where('posTransaction', '==', true),
          where('tranTypeCode', '==', 'PS')
        );
        const snap = await getDocs(q);
        const list = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(t => {
            if (activeCounterId && t.counterId !== activeCounterId) return false;
            if (activeBranchId && activeBranchId !== 'all' && t.branchId !== activeBranchId) return false;
            if (t.status === 'Cancelled') return false;
            return true;
          })
          .sort((a, b) => {
            const da = a.createdAt || a.docDate || '';
            const db2 = b.createdAt || b.docDate || '';
            return db2.localeCompare(da);
          });
        setTransactions(list);
      } catch (err) {
        console.error('Picker load failed:', err);
      } finally {
        setLoading(false);
        setTimeout(() => searchRef.current?.focus(), 100);
      }
    })();
  }, [isOpen, companyId, activeBranchId, activeCounterId]);

  // Esc closes; Enter on the selected row confirms.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose?.(); }
      if (e.key === 'Enter' && selectedId) {
        const t = transactions.find(x => x.id === selectedId);
        if (t) { e.preventDefault(); onSelect?.(t); }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, selectedId, transactions, onSelect, onClose]);

  // Build date cutoff once per preset change.
  const dateCutoff = useMemo(() => {
    const preset = DATE_PRESETS.find(p => p.id === datePreset);
    if (!preset || preset.days === null) return null;
    const d = new Date();
    d.setDate(d.getDate() - preset.days);
    return localDateStr(d);
  }, [datePreset]);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    const qc = searchCustomer.trim().toLowerCase();
    const qa = Number(searchAmount) || 0;
    return transactions.filter(t => {
      if (dateCutoff && datePreset === 'today') {
        if (t.docDate !== dateCutoff) return false;
      } else if (dateCutoff && datePreset === 'y') {
        // exactly yesterday's date
        const y = new Date();
        y.setDate(y.getDate() - 1);
        if (t.docDate !== localDateStr(y)) return false;
      } else if (dateCutoff) {
        // last N days
        if ((t.docDate || '') < dateCutoff) return false;
      }
      if (q) {
        const blob = `${t.docNumber || ''} ${t.partyName || ''} ${t.totalAmount || ''}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      if (qc && !(t.partyName || '').toLowerCase().includes(qc)) return false;
      if (qa > 0) {
        // Match if total within ±1 of the entered amount — typing 500 should
        // match both 500 and 499.50, the kind of round number a cashier remembers.
        const amt = Number(t.totalAmount) || 0;
        if (Math.abs(amt - qa) > 1) return false;
      }
      return true;
    });
  }, [transactions, searchText, searchCustomer, searchAmount, datePreset, dateCutoff]);

  const selectedTxn = transactions.find(t => t.id === selectedId);

  const clearFilters = () => { setSearchText(''); setSearchCustomer(''); setSearchAmount(''); setDatePreset('today'); };

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header $mode={mode}>
          <HeaderLeft>
            {mode === 'exchange' ? <FiRepeat size={20} /> : <FiRotateCcw size={20} />}
            <div>
              <HeaderTitle>{mode === 'exchange' ? 'Select Receipt to Exchange' : 'Select Receipt to Return'}</HeaderTitle>
              <HeaderSub>Tap a row to select · Enter or SELECT to confirm</HeaderSub>
            </div>
          </HeaderLeft>
          <CloseBtn onClick={onClose}><FiX size={22} /></CloseBtn>
        </Header>

        <FilterArea>
          <SearchRow>
            <SearchBox>
              <FiSearch size={16} />
              <SearchInp
                ref={searchRef}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder="Search by receipt#, customer or amount…"
              />
              {searchText && <ClearBtn onClick={() => setSearchText('')}><FiX size={14} /></ClearBtn>}
            </SearchBox>
          </SearchRow>

          <SearchRow $grid>
            <FieldBox>
              <FieldIcon><FiHash size={14} /></FieldIcon>
              <SmallInp
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder="Receipt # (e.g. 00042)"
              />
            </FieldBox>
            <FieldBox>
              <FieldIcon><FiUser size={14} /></FieldIcon>
              <SmallInp
                value={searchCustomer}
                onChange={e => setSearchCustomer(e.target.value)}
                placeholder="Customer name"
              />
            </FieldBox>
            <FieldBox>
              <FieldIcon><FiDollarSign size={14} /></FieldIcon>
              <SmallInp
                type="number"
                value={searchAmount}
                onChange={e => setSearchAmount(e.target.value)}
                placeholder="Exact amount"
                inputMode="decimal"
              />
            </FieldBox>
          </SearchRow>

          <ChipRow>
            <ChipLabel><FiCalendar size={12} /> Date:</ChipLabel>
            {DATE_PRESETS.map(p => (
              <Chip key={p.id} $active={datePreset === p.id} onClick={() => setDatePreset(p.id)}>
                {p.label}
              </Chip>
            ))}
            <Spacer />
            <ResetChip onClick={clearFilters}>Clear filters</ResetChip>
          </ChipRow>
        </FilterArea>

        <ListWrap>
          {loading ? (
            <Empty>Loading transactions…</Empty>
          ) : filtered.length === 0 ? (
            <Empty>
              No receipts match.<br />
              <small>Try a different date range or clear filters.</small>
            </Empty>
          ) : filtered.map(t => {
            const isSel = selectedId === t.id;
            const created = t.createdAt ? new Date(t.createdAt) : null;
            const time = created ? created.toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' }) : '';
            const payLabel = t.paymentMethod === 'split' ? 'Split'
              : t.paymentMethod === 'credit' ? 'Credit'
              : t.paymentMethod === 'card' ? 'Card'
              : t.paymentMethod === 'wallet' ? 'Wallet'
              : t.paymentMethod === 'bank' ? 'Bank'
              : 'Cash';
            return (
              <Row
                key={t.id}
                $selected={isSel}
                onClick={() => setSelectedId(t.id)}
                onDoubleClick={() => onSelect?.(t)}
              >
                <RowTop>
                  <DocNum>{t.docNumber}</DocNum>
                  <PayBadge>{payLabel}</PayBadge>
                  <Amount>{formatCurrency(t.totalAmount || 0)}</Amount>
                </RowTop>
                <RowBot>
                  <span>{t.partyName || 'Walk-in Customer'}</span>
                  <span>{t.docDate} {time && '· ' + time}</span>
                  <span>{(t.lines?.length || 0)} item{(t.lines?.length || 0) !== 1 ? 's' : ''}</span>
                </RowBot>
                {isSel && <SelectedBar><FiCheck size={14} /> Selected</SelectedBar>}
              </Row>
            );
          })}
        </ListWrap>

        <Footer>
          <CountText>{filtered.length} receipt{filtered.length !== 1 ? 's' : ''}</CountText>
          <CancelBtn onClick={onClose}>Cancel</CancelBtn>
          <ConfirmBtn
            $mode={mode}
            disabled={!selectedTxn}
            onClick={() => selectedTxn && onSelect?.(selectedTxn)}
          >
            <FiCheck size={18} />
            {mode === 'exchange' ? 'EXCHANGE THIS RECEIPT' : 'RETURN THIS RECEIPT'}
          </ConfirmBtn>
        </Footer>
      </Modal>
    </Overlay>
  );
};

// ─── Styled ──────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex; align-items: center; justify-content: center;
  z-index: 1100;
`;
const Modal = styled.div`
  background: white;
  border-radius: ${borderRadius.lg};
  width: 760px; max-width: 95vw;
  max-height: 90vh;
  display: flex; flex-direction: column;
  box-shadow: ${shadows.xl};
  overflow: hidden;
`;
const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 20px;
  color: white;
  background: ${({ $mode }) => $mode === 'exchange' ? '#7C3AED' : colors.error.main};
`;
const HeaderLeft = styled.div`
  display: flex; align-items: center; gap: 12px;
`;
const HeaderTitle = styled.div`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  letter-spacing: 0.3px;
`;
const HeaderSub = styled.div`
  font-size: ${typography.fontSize.xs};
  opacity: 0.85;
  margin-top: 2px;
`;
const CloseBtn = styled.button`
  background: transparent;
  border: 1px solid rgba(255,255,255,0.3);
  color: white;
  width: 34px; height: 34px;
  display: flex; align-items: center; justify-content: center;
  border-radius: ${borderRadius.sm};
  cursor: pointer;
  &:hover { background: rgba(255,255,255,0.18); }
`;
const FilterArea = styled.div`
  padding: 12px 20px;
  border-bottom: 1px solid ${colors.border.light};
  background: ${colors.neutral[50]};
  display: flex; flex-direction: column; gap: 8px;
`;
const SearchRow = styled.div`
  display: ${({ $grid }) => $grid ? 'grid' : 'flex'};
  grid-template-columns: ${({ $grid }) => $grid ? 'repeat(3, 1fr)' : 'auto'};
  gap: 8px;
  align-items: center;
`;
const SearchBox = styled.div`
  flex: 1;
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px;
  background: white;
  border: 2px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  color: ${colors.text.secondary};
  &:focus-within { border-color: ${colors.primary.main}; }
`;
const SearchInp = styled.input`
  flex: 1;
  border: none; outline: none;
  font-size: ${typography.fontSize.md};
  background: transparent;
  &::placeholder { color: ${colors.text.tertiary}; }
`;
const ClearBtn = styled.button`
  background: none; border: none; padding: 2px;
  color: ${colors.text.tertiary}; cursor: pointer;
  &:hover { color: ${colors.text.primary}; }
`;
const FieldBox = styled.div`
  display: flex; align-items: center;
  background: white;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  padding: 0 10px;
  &:focus-within { border-color: ${colors.primary.main}; }
`;
const FieldIcon = styled.div`
  display: flex; color: ${colors.text.tertiary};
`;
const SmallInp = styled.input`
  flex: 1;
  border: none; outline: none;
  padding: 8px 10px;
  font-size: ${typography.fontSize.sm};
  background: transparent;
  &::placeholder { color: ${colors.text.tertiary}; }
`;
const ChipRow = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: 6px;
`;
const ChipLabel = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.secondary};
  margin-right: 4px;
`;
const Chip = styled.button`
  padding: 6px 12px;
  border-radius: 16px;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  border: 2px solid ${({ $active }) => $active ? colors.primary.main : colors.border.light};
  background: ${({ $active }) => $active ? colors.primary.main + '15' : 'white'};
  color: ${({ $active }) => $active ? colors.primary.main : colors.text.secondary};
  cursor: pointer;
  &:hover { border-color: ${colors.primary.main}; }
`;
const Spacer = styled.div` flex: 1; `;
const ResetChip = styled.button`
  padding: 6px 12px;
  border-radius: 16px;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  border: none;
  background: transparent;
  color: ${colors.text.secondary};
  cursor: pointer;
  &:hover { color: ${colors.error.main}; text-decoration: underline; }
`;
const ListWrap = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 240px;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: ${colors.neutral[300]}; border-radius: 4px; }
`;
const Empty = styled.div`
  display: flex; align-items: center; justify-content: center;
  height: 100%;
  flex-direction: column; text-align: center;
  padding: 40px;
  color: ${colors.text.tertiary};
  font-size: ${typography.fontSize.sm};
  small { display: block; margin-top: 6px; font-size: ${typography.fontSize.xs}; }
`;
const Row = styled.div`
  position: relative;
  padding: 14px 20px;
  cursor: pointer;
  border-bottom: 1px solid ${colors.neutral[100]};
  background: ${({ $selected }) => $selected ? colors.primary.main + '12' : 'transparent'};
  border-left: 4px solid ${({ $selected }) => $selected ? colors.primary.main : 'transparent'};
  transition: background 0.1s;
  &:hover { background: ${({ $selected }) => $selected ? colors.primary.main + '18' : colors.neutral[50]}; }
`;
const RowTop = styled.div`
  display: flex; align-items: center; gap: 12px;
`;
const DocNum = styled.span`
  font-size: ${typography.fontSize.md};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.primary};
  min-width: 140px;
`;
const PayBadge = styled.span`
  display: inline-flex;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: ${typography.fontWeight.bold};
  background: ${colors.neutral[100]};
  color: ${colors.text.secondary};
`;
const Amount = styled.span`
  margin-left: auto;
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.primary};
`;
const RowBot = styled.div`
  display: flex; gap: 18px; margin-top: 4px;
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.tertiary};
`;
const SelectedBar = styled.div`
  display: inline-flex; align-items: center; gap: 4px;
  margin-top: 6px;
  padding: 2px 8px;
  background: ${colors.primary.main};
  color: white;
  border-radius: 4px;
  font-size: 10px;
  font-weight: ${typography.fontWeight.bold};
  letter-spacing: 0.5px;
`;
const Footer = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 12px 20px;
  border-top: 1px solid ${colors.border.light};
  background: white;
`;
const CountText = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.tertiary};
  margin-right: auto;
`;
const CancelBtn = styled.button`
  padding: 12px 22px;
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  background: white;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  cursor: pointer;
  &:hover { background: ${colors.neutral[100]}; }
`;
const ConfirmBtn = styled.button`
  display: inline-flex; align-items: center; gap: 8px;
  padding: 12px 22px;
  border: none;
  border-radius: ${borderRadius.md};
  background: ${({ $mode }) => $mode === 'exchange' ? '#7C3AED' : colors.error.main};
  color: white;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.bold};
  letter-spacing: 0.5px;
  cursor: pointer;
  &:hover:not(:disabled) { filter: brightness(0.92); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

export default POSReceiptPickerModal;
